import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { langFromPath, hasLangPrefix, isLocalizedPath, localizePath, prefixOf } from '@/lib/langPath';

/**
 * Mantiene toda la navegación dentro de la dirección del idioma elegido:
 * reescribe los enlaces internos en el momento del clic y corrige cualquier
 * navegación que se quede sin prefijo (por ejemplo, las que se disparan desde
 * un botón y no desde un enlace).
 */
export default function LanguageRouterGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(location.pathname);
  const prefix = prefixOf(lang);

  useEffect(() => {
    if (!prefix) return undefined;

    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!anchor) return;

      const target = anchor.getAttribute('target');
      if (target && target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return;
      if (hasLangPrefix(href)) return;

      const pathname = href.split(/[?#]/)[0] || '/';
      if (!isLocalizedPath(pathname)) return;

      event.preventDefault();
      event.stopPropagation();
      navigate(localizePath(href, lang));
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [lang, prefix, navigate]);

  useEffect(() => {
    if (!prefix) return;

    const { pathname, search, hash } = location;
    if (hasLangPrefix(pathname) || !isLocalizedPath(pathname)) return;

    navigate(localizePath(pathname + search + hash, lang), { replace: true });
  }, [lang, prefix, location, navigate]);

  return null;
}